import { z } from "zod";

/**
 * Subscriptions (spec §9): RevenueCat is the single source of truth. The server never trusts a purchase flag from
 * a client; it reads the subscriber from RevenueCat (after every webhook and on demand) and caches the result in
 * `entitlements`. This module turns RevenueCat's subscriber record into StudyWard's subscription state.
 */

/** The RevenueCat entitlement that unlocks Pro (create it with this identifier in the RevenueCat project). */
export const PRO_ENTITLEMENT_ID = "study_ward_pro";

export const SUBSCRIPTION_STATUSES = ["trial", "active", "grace", "billing_retry", "cancelled", "expired", "revoked"] as const;
export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

/** The parts of RevenueCat's v1 subscriber response we use. */
const rcDate = z.string().nullable().optional();
export const rcSubscriberSchema = z.object({
  subscriber: z.object({
    entitlements: z.record(z.string(), z.object({ expires_date: rcDate, product_identifier: z.string(), grace_period_expires_date: rcDate })).default({}),
    subscriptions: z
      .record(
        z.string(),
        z.object({
          expires_date: rcDate,
          period_type: z.string().optional(),
          unsubscribe_detected_at: rcDate,
          billing_issues_detected_at: rcDate,
          refunded_at: rcDate,
          grace_period_expires_date: rcDate,
          store: z.string().optional(),
          is_sandbox: z.boolean().optional(),
        }),
      )
      .default({}),
    management_url: z.string().nullable().optional(),
  }),
});
export type RcSubscriber = z.infer<typeof rcSubscriberSchema>;

export interface EntitlementState {
  status: SubscriptionStatus;
  product_id: string;
  current_period_end: string | null;
  will_renew: boolean;
  store: string | null;
  is_sandbox: boolean;
  management_url: string | null;
}

const time = (d: string | null | undefined) => (d ? Date.parse(d) : null);

/**
 * Subscription lifecycle (§9): trial → active → renewals; cancellation keeps Pro to the period end; a billing
 * problem keeps Pro through the store's grace period and billing retry (with a fix-payment prompt, not a lockout);
 * a refund revokes it; expiry ends it. Returns null when the user has never had Pro.
 */
export function entitlementFromSubscriber(rc: RcSubscriber, now: Date): EntitlementState | null {
  const ent = rc.subscriber.entitlements[PRO_ENTITLEMENT_ID];
  if (!ent) return null;
  const sub = rc.subscriber.subscriptions[ent.product_identifier];
  const expires = time(ent.expires_date ?? sub?.expires_date);
  const grace = time(ent.grace_period_expires_date ?? sub?.grace_period_expires_date);
  const t = now.getTime();
  const base = {
    product_id: ent.product_identifier,
    store: sub?.store ?? null,
    is_sandbox: sub?.is_sandbox ?? false,
    management_url: rc.subscriber.management_url ?? null,
  };
  const at = (ms: number | null) => (ms === null ? null : new Date(ms).toISOString());

  if (sub?.refunded_at) return { ...base, status: "revoked", current_period_end: at(expires), will_renew: false };
  // Lifetime / non-expiring purchase.
  if (expires === null) return { ...base, status: "active", current_period_end: null, will_renew: false };
  const billingIssue = !!sub?.billing_issues_detected_at;
  const cancelled = !!sub?.unsubscribe_detected_at;
  if (expires > t) {
    if (billingIssue) return { ...base, status: grace && grace > t ? "grace" : "billing_retry", current_period_end: at(grace && grace > expires ? grace : expires), will_renew: true };
    if (cancelled) return { ...base, status: "cancelled", current_period_end: at(expires), will_renew: false };
    return { ...base, status: sub?.period_type === "trial" ? "trial" : "active", current_period_end: at(expires), will_renew: true };
  }
  if (grace && grace > t) return { ...base, status: "grace", current_period_end: at(grace), will_renew: true };
  // The store is still retrying the payment: keep the student studying, prompt them to fix it (§9).
  if (billingIssue && !cancelled) return { ...base, status: "billing_retry", current_period_end: at(expires), will_renew: true };
  return { ...base, status: "expired", current_period_end: at(expires), will_renew: false };
}

/** RevenueCat webhook body (the fields we use; the whole payload is logged to payment_events). */
export const rcWebhookSchema = z.object({
  api_version: z.string().optional(),
  event: z.object({
    id: z.string().min(1),
    type: z.string().min(1),
    app_user_id: z.string().optional(),
    original_app_user_id: z.string().optional(),
    aliases: z.array(z.string()).optional(),
    transferred_from: z.array(z.string()).optional(),
    transferred_to: z.array(z.string()).optional(),
    environment: z.string().optional(),
    event_timestamp_ms: z.number().optional(),
  }),
});
export type RcWebhook = z.infer<typeof rcWebhookSchema>;

/** Every StudyWard user an event may touch (app user ids are Supabase user ids). */
export function webhookUserIds(body: RcWebhook): string[] {
  const e = body.event;
  const ids = [e.app_user_id, e.original_app_user_id, ...(e.aliases ?? []), ...(e.transferred_from ?? []), ...(e.transferred_to ?? [])];
  return [...new Set(ids.filter((id): id is string => !!id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)))];
}

/** What the app shows in Me → Subscription and uses after a purchase (part of GET /billing/entitlement). */
export const subscriptionSchema = z.object({
  status: subscriptionStatusSchema,
  source: z.enum(["revenuecat", "admin_comp"]),
  product_id: z.string(),
  current_period_end: z.string().nullable(),
  will_renew: z.boolean(),
  store: z.string().nullable(),
  management_url: z.string().nullable(),
});
export type Subscription = z.infer<typeof subscriptionSchema>;
